/**
 * QueryConsole Layout Store - Manages layout state for the Query Console workspace.
 *
 * Persists view mode (Builder/Console/Saved), DataTree visibility, and the
 * orientation/order/sizes of the inner Console+Output split. Sizes were
 * previously component-local and reset on every reload; persisting them lets
 * a user's chosen layout survive sessions.
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"

export type QueryConsoleViewMode =
  | "builder"
  | "transform"
  | "console"
  | "saved"
export type ConsoleOutputDirection = "vertical" | "horizontal"
export type ConsoleOutputOrder = "console-first" | "output-first"
/** Density of the Transform timeline. Compact collapses each step to a one-liner. */
export type TransformDensity = "full" | "compact"

/** When a structured (pipeline/builder) record is opened as text in the Console
 *  editor, this captures where it came from so the editor can show a banner
 *  and the save flow can default to "save as new script" rather than
 *  overwriting the structured record on disk. */
export interface ConsoleStructuredOrigin {
  name: string
  kind: "pipeline" | "builder"
}

interface QueryConsoleLayoutStore {
  viewMode: QueryConsoleViewMode
  dataTreeVisible: boolean
  consoleOutputDirection: ConsoleOutputDirection
  consoleOutputOrder: ConsoleOutputOrder
  horizontalSplitSizes: [number, number]
  consoleSplitSizes: [number, number]
  isMaximized: boolean
  transformDensity: TransformDensity
  /** Non-null while the Console editor is showing a pipeline/builder script. */
  consoleStructuredOrigin: ConsoleStructuredOrigin | null
  /** The "Edit anyway" toggle — false keeps the editor read-only so the user
   *  can't accidentally drift off the structured representation. */
  consoleStructuredEditable: boolean

  setViewMode: (mode: QueryConsoleViewMode) => void
  toggleDataTree: () => void
  toggleDirection: () => void
  toggleOrder: () => void
  setHorizontalSplitSizes: (sizes: number[]) => void
  setConsoleSplitSizes: (sizes: number[]) => void
  toggleMaximized: () => void
  setMaximized: (maximized: boolean) => void
  toggleTransformDensity: () => void
  setConsoleStructuredOrigin: (origin: ConsoleStructuredOrigin | null) => void
  setConsoleStructuredEditable: (editable: boolean) => void
  resetLayout: () => void
}

const DEFAULT_LAYOUT = {
  viewMode: "console" as QueryConsoleViewMode,
  dataTreeVisible: true,
  consoleOutputDirection: "vertical" as ConsoleOutputDirection,
  consoleOutputOrder: "console-first" as ConsoleOutputOrder,
  horizontalSplitSizes: [25, 75] as [number, number],
  consoleSplitSizes: [50, 50] as [number, number],
  isMaximized: false,
  transformDensity: "full" as TransformDensity,
  consoleStructuredOrigin: null as ConsoleStructuredOrigin | null,
  consoleStructuredEditable: false,
}

const toPair = (sizes: number[]): [number, number] => {
  if (sizes.length >= 2) return [sizes[0], sizes[1]]
  return DEFAULT_LAYOUT.horizontalSplitSizes
}

export const useQueryConsoleLayoutStore = create<QueryConsoleLayoutStore>()(
  persist(
    (set) => ({
      ...DEFAULT_LAYOUT,

      setViewMode: (mode) => set({ viewMode: mode }),
      toggleDataTree: () =>
        set((state) => ({ dataTreeVisible: !state.dataTreeVisible })),
      toggleDirection: () =>
        set((state) => ({
          consoleOutputDirection:
            state.consoleOutputDirection === "vertical"
              ? "horizontal"
              : "vertical",
        })),
      toggleOrder: () =>
        set((state) => ({
          consoleOutputOrder:
            state.consoleOutputOrder === "console-first"
              ? "output-first"
              : "console-first",
        })),
      setHorizontalSplitSizes: (sizes) =>
        set({ horizontalSplitSizes: toPair(sizes) }),
      setConsoleSplitSizes: (sizes) =>
        set({ consoleSplitSizes: toPair(sizes) }),
      toggleMaximized: () =>
        set((state) => ({ isMaximized: !state.isMaximized })),
      setMaximized: (maximized) => set({ isMaximized: maximized }),
      toggleTransformDensity: () =>
        set((state) => ({
          transformDensity:
            state.transformDensity === "full" ? "compact" : "full",
        })),
      setConsoleStructuredOrigin: (origin) =>
        set({
          consoleStructuredOrigin: origin,
          // Whenever the origin changes (set or cleared), reset editability —
          // re-opening a structured record always starts read-only.
          consoleStructuredEditable: false,
        }),
      setConsoleStructuredEditable: (editable) =>
        set({ consoleStructuredEditable: editable }),
      resetLayout: () => set({ ...DEFAULT_LAYOUT }),
    }),
    {
      name: "dataq-query-console-layout",
      version: 1,
      // v0→v1: reset viewMode to "console" on first load after upgrade.
      // The Builder/Transform tabs are powerful but Console remains the
      // central editing surface, so we want it to be the landing view for
      // every user regardless of what they previously had selected.
      // Other persisted preferences (split sizes, density, etc.) are
      // preserved.
      migrate: (persistedState, fromVersion) => {
        const state =
          (persistedState as Partial<QueryConsoleLayoutStore> | undefined) ?? {}
        if (fromVersion < 1) {
          return { ...state, viewMode: "console" } as QueryConsoleLayoutStore
        }
        return state as QueryConsoleLayoutStore
      },
      partialize: (state) => ({
        viewMode: state.viewMode,
        dataTreeVisible: state.dataTreeVisible,
        consoleOutputDirection: state.consoleOutputDirection,
        consoleOutputOrder: state.consoleOutputOrder,
        horizontalSplitSizes: state.horizontalSplitSizes,
        consoleSplitSizes: state.consoleSplitSizes,
        isMaximized: state.isMaximized,
        transformDensity: state.transformDensity,
      }),
    }
  )
)
